import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { useBrand } from '@/brand/context'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { looksLikeEmail, useBlurValidation } from '@/ui/validation'
import { PublicPage } from './PublicPage'

/**
 * `/check-email` — S-D2 screen 07.
 *
 * A different page from `/verify-email`, which is the *token* page an emailed
 * link lands on. This is the one the person who just signed up is looking at
 * while they go and find the message, and the distinction matters because the
 * two have opposite jobs: one redeems a token, this one says a token is on its
 * way and offers to send another.
 *
 * **The account works without it**, and the screen says so, because S-D2 says
 * so: "You can explore while you wait, but publishing and outreach stay locked
 * until your email is confirmed." Blocking the product on a mail round trip is
 * how a trial is lost to a spam filter.
 *
 * *Resend* is `POST /auth/email/resend`, which is **authenticated** — the
 * unauthenticated form would be an oracle for which addresses have accounts, for
 * anyone willing to watch their own mail server. Signup leaves a session, so
 * this screen always has one.
 *
 * **"Use a different address" is here rather than in settings** (AUTH-12).
 * Screen 07 draws it, and the reason it belongs on *this* screen is the state
 * the reader is in: they have just typed an address, the message has not
 * arrived, and the likeliest explanation is a typo in what they typed. Sending
 * them to a settings page to fix it means finding a settings page, and the
 * account that most needs this has no organisation for one to hang off yet.
 * `POST /auth/email/change` already existed with no caller in the app but the
 * token page that redeems what it sends.
 */
export function CheckEmail(): JSX.Element {
  const brand = useBrand()
  const navigate = useNavigate()
  const { user, status, refresh } = useAuth()
  const [sentAgain, setSentAgain] = useState(false)
  const [changing, setChanging] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [changedTo, setChangedTo] = useState<string | null>(null)

  const resend = useMutation(() => call(() => api.POST('/api/v1/auth/email/resend', {})))
  const change = useMutation((body: { email: string; password?: string }) =>
    call(() => api.POST('/api/v1/auth/email/change', { body })),
  )

  const trimmedNew = newEmail.trim()
  const emailRule = useBlurValidation(
    trimmedNew === ''
      ? 'Enter the address you want the account on.'
      : looksLikeEmail(trimmedNew)
        ? undefined
        : 'That does not look like an email address — check for a typo.',
  )

  async function onResend(): Promise<void> {
    const result = await resend.run(undefined)
    setSentAgain(result.ok)
  }

  async function onChangeAddress(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (emailRule.error !== undefined) {
      emailRule.onBlur()
      return
    }
    const address = trimmedNew.toLowerCase()
    const result = await change.run(
      user?.has_password ? { email: address, password: currentPassword } : { email: address },
    )
    if (!result.ok) return
    // The address on the account does not move until the new one is confirmed
    // (`redeem_email_change`), so `refresh()` is what puts the screen back in
    // step: `user.email` is still the old one and the sentence below has to say
    // so rather than claiming a change that has not happened yet.
    setChanging(false)
    setNewEmail('')
    setCurrentPassword('')
    setSentAgain(false)
    setChangedTo(address)
    await refresh()
  }

  async function onContinue(): Promise<void> {
    // Re-read first: the address may have been confirmed in another tab, and
    // carrying on with a stale "unverified" would show the wrong banner.
    await refresh()
    void navigate(DEFAULT_HOME, { replace: true })
  }

  if (status === 'anonymous') {
    return (
      <PublicPage title="Check your inbox">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            Open the confirmation link we emailed you. If you need a new one, sign in first.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in
          </Link>
        </div>
      </PublicPage>
    )
  }

  if (changing) {
    return (
      <PublicPage
        title="Use a different address"
        footer={
          <>
            Found it after all?{' '}
            <button
              type="button"
              className="app-link-button"
              onClick={() => {
                setChanging(false)
                change.reset()
              }}
            >
              Back to your inbox
            </button>
          </>
        }
      >
        {/* `method="post"` so a submit with dead JavaScript cannot put this
            password in the query string. Reasoning in full on `Login.tsx`. */}
        <form
          method="post"
          onSubmit={(event) => {
            void onChangeAddress(event)
          }}
        >
          <div className="ar-card__body ar-stack">
            <p className="ar-muted" style={{ margin: 0 }}>
              We will send the confirmation link to the new address. Nothing changes on the account
              until you open it.
            </p>
            <InlineError failure={change.state.failure} takesFocus />
            <TextField
              label="New email address"
              type="email"
              required
              autoFocus
              autoComplete="email"
              placeholder="you@company.in"
              error={emailRule.shown}
              value={newEmail}
              onBlur={emailRule.onBlur}
              onChange={(event) => {
                setNewEmail(event.target.value)
                change.reset()
              }}
            />
            {/* Only when there is one to ask for — a Google-only identity has
                never set a password and the server skips the check for it
                (`_require_current_password`). */}
            {user?.has_password ? (
              <TextField
                label="Current password"
                type="password"
                required
                autoComplete="current-password"
                help="Confirms it is you moving the account to a different address."
                value={currentPassword}
                onChange={(event) => {
                  setCurrentPassword(event.target.value)
                  change.reset()
                }}
              />
            ) : null}

            {/* AUTH-34, on the in-place form AUTH-12 needed: the same frame the
                eleven drawn screens share, rather than the one auth screen with
                a bordered footer rail across it. */}
            <Button
              tone="primary"
              size="lg"
              block
              type="submit"
              busy={change.pending}
              busyLabel="Sending the link"
            >
              Send the link here instead
            </Button>
          </div>
        </form>
      </PublicPage>
    )
  }

  /*
    A change that has been asked for but not yet confirmed is its **own screen**,
    not a banner on this one.

    It has to be, because after `POST /auth/email/change` the account still holds
    the old address — `redeem_email_change` is what moves it — and every sentence
    on the ordinary view is then about the wrong address. Rendered as a banner
    above the unchanged body, the screen said "the link is on its way to
    priya@kavya.in" directly above "we sent a link to priya@kavyainteriors.in",
    and offered *Resend email*, which is `POST /auth/email/resend` and mails the
    **account's** address — so the one button on screen would have sent another
    message to the address the reader had just finished correcting.

    So the pending address becomes the subject, the old one is named as what the
    account still has, and *Resend* is not offered at all: there is no endpoint
    that re-sends to a pending address, and the honest way to get another is to
    ask for the change again, which is what the button below does.
  */
  if (changedTo) {
    return (
      /* AUTH-52's shape, on the state that shares it: one title, and it is the
         empty state's, so this screen does not print "Check your inbox" above
         "Confirm your new address" either. */
      <PublicPage footer="You can explore while you wait, but publishing and outreach stay locked until your email is confirmed.">
        <div className="ar-card__body ar-stack">
          <div className="ar-empty">
            <div className="ar-empty__icon" aria-hidden="true">
              ✉
            </div>
            <h1 className="ar-empty__title">Confirm your new address</h1>
            {/* Two sentences, two elements, and deliberately not merged into
                one. SF-15's re-verification of this screen found it naming the
                *old* address as where the link had gone; the assertion that
                caught it reads the element containing "we sent a link to" and
                requires the account's current address not to be in it. One
                paragraph carrying both addresses passes that test only by
                accident of wording. */}
            <span className="ar-empty__body">
              We sent a link to <strong>{changedTo}</strong>. It works once and lasts 24 hours.
            </span>
            <span className="ar-empty__body">
              The account still uses <strong>{user?.email ?? 'your old address'}</strong> and keeps
              it until you open that link.
            </span>
            <div className="ar-btn-group" style={{ justifyContent: 'center' }}>
              <Button
                tone="quiet"
                size="sm"
                onClick={() => {
                  setChanging(true)
                  setChangedTo(null)
                }}
              >
                Use a different address
              </Button>
            </div>
          </div>
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button
            tone="primary"
            onClick={() => {
              void onContinue()
            }}
          >
            Continue to {brand.productName}
          </Button>
        </div>
      </PublicPage>
    )
  }

  return (
    /*
      AUTH-52. No `h1` above the card. Screen 07 has one title and it is the
      empty state's — the app printed "Check your inbox" above the card *and*
      "Confirm your email address" inside it, which is two headings for one
      screen and neither of them the whole message.

      The state's title becomes the `h1`, so the document still starts at level
      1. That is not a detail: this screen is reached from an email, often as
      the first AugRank page somebody sees, and a page whose headings start at
      `<p>` gives a screen-reader user nothing to jump to.

      AUTH-55: the footer note is the design's — it opens with what you *can*
      do rather than with what is locked.
    */
    <PublicPage footer="You can explore while you wait, but publishing and outreach stay locked until your email is confirmed.">
      <div className="ar-card__body ar-stack">
        <InlineError failure={resend.state.failure} />
        {sentAgain ? (
          <div className="ar-alert ar-alert--success" role="status">
            <span className="ar-alert__icon" aria-hidden="true">
              ✓
            </span>
            <div className="ar-alert__body">
              <strong>A new link is on its way</strong>
              The previous link stopped working the moment this one was issued.
            </div>
          </div>
        ) : null}

        {/*
          AUTH-53 / AUTH-54. The actions belong to the empty state, inside it
          and centred, at `sm` — where the app had them in a bordered card
          footer at `md`. Screen 07 has no footer on this card, and the reason
          shows in what the footer was doing to the screen: three equal-weight
          buttons in a row under a rule, where the design has two small ones
          under the sentence that explains them and nothing else competing.

          The standalone "Nothing after a few minutes?" paragraph is gone with
          it (AUTH-54). It was a fourth block in a stack the design gives one,
          and *Resend email* is directly under the sentence it was advising
          about, so the advice was telling somebody to do the thing they were
          already looking at.
        */}
        <div className="ar-empty">
          <div className="ar-empty__icon" aria-hidden="true">
            ✉
          </div>
          <h1 className="ar-empty__title">Check your inbox</h1>
          <span className="ar-empty__body">
            We sent a confirmation link to <strong>{user?.email ?? 'your address'}</strong>. It
            works once and lasts 24 hours — check spam if nothing arrives.
          </span>
          <div className="ar-btn-group" style={{ justifyContent: 'center' }}>
            <Button
              size="sm"
              busy={resend.pending}
              busyLabel="Sending"
              onClick={() => {
                void onResend()
              }}
            >
              Resend email
            </Button>
            {/* AUTH-12. Quiet, because it is the third choice on this screen:
                most people are waiting for a message that is coming, some need
                it sent again, and a few typed the address wrong. */}
            <Button
              tone="quiet"
              size="sm"
              onClick={() => {
                setChanging(true)
                setChangedTo(null)
                resend.reset()
              }}
            >
              Use a different address
            </Button>
          </div>
        </div>
      </div>
      {/* "Continue to AugRank" is an app addition the mockup does not draw —
          §4 lets an unverified account use everything but publishing and
          outreach, so there has to be a way off this screen that is not the
          browser's back button. It keeps the card footer to itself, which is
          what a footer is for: one action, about the card above it. */}
      <div className="ar-card__footer">
        <span className="ar-card__spacer" />
        <Button
          tone="primary"
          onClick={() => {
            void onContinue()
          }}
        >
          Continue to {brand.productName}
        </Button>
      </div>
    </PublicPage>
  )
}
